import type { MemberSearchResult } from '@yawi/domain';
import type { HikCentralImportPreviewRow } from '@yawi/types';
import { Loader2, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface MemberSearchCellProps {
  row: HikCentralImportPreviewRow;
  value: string;
  valueLabel: string;
  onChange: (memberId: string, displayName: string) => void;
  onSearch: (query: string) => Promise<MemberSearchResult[]>;
  disabled?: boolean;
}

const CRITICAL_WARNINGS = ['sin nombre', 'fila ignorable'];

function hasCriticalWarning(warnings: string[]) {
  return warnings.some((w) => CRITICAL_WARNINGS.some((cw) => w.toLowerCase().includes(cw)));
}

export function MemberSearchCell({ row, value, valueLabel, onChange, onSearch, disabled }: MemberSearchCellProps) {
  const isCritical = hasCriticalWarning(row.warnings);

  const [inputValue, setInputValue] = useState(valueLabel);
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<MemberSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(valueLabel);
  }, [valueLabel]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        // Restore to current selected label on blur without selection
        setInputValue(valueLabel);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [valueLabel]);

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value;
    setInputValue(q);
    setIsOpen(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const found = await onSearch(q.trim());
        setResults(found);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }

  function handleFocus() {
    setIsOpen(true);
    if (!inputValue && results.length === 0) {
      // Show suggestions as initial options
      const initialResults = row.suggestions.map((s) => ({ id: s.memberId, displayName: s.displayName }));
      setResults(initialResults);
    }
  }

  function handleSelect(member: MemberSearchResult) {
    setInputValue(member.displayName);
    setIsOpen(false);
    setResults([]);
    onChange(member.id, member.displayName);
  }

  function handleClear() {
    setInputValue('');
    setResults([]);
    setIsOpen(false);
    onChange('', '');
  }

  if (isCritical) {
    return <span className="text-xs text-slate-400 italic">Sin nombre — omitida</span>;
  }

  return (
    <div ref={containerRef} className="relative w-full min-w-[200px]">
      <div className="relative flex items-center">
        <Search className="absolute left-2.5 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          disabled={disabled}
          placeholder="Buscar miembro..."
          className="w-full pl-8 pr-7 py-1.5 text-sm border border-slate-200 rounded-lg bg-white dark:bg-slate-800 dark:border-slate-600 text-theme placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {value && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); handleClear(); }}
            className="absolute right-2 p-0.5 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        {isSearching && (
          <Loader2 className="absolute right-2 w-3.5 h-3.5 text-slate-400 animate-spin" />
        )}
      </div>

      {isOpen && (results.length > 0 || isSearching) && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {isSearching ? (
            <div className="px-3 py-2 text-xs text-slate-400 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" />
              Buscando...
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">Sin resultados</div>
          ) : (
            results.map((member) => (
              <button
                key={member.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(member); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors ${
                  value === member.id ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 font-medium' : 'text-theme'
                }`}
              >
                {member.displayName}
                {row.suggestions.find((s) => s.memberId === member.id) && (
                  <span className="ml-1.5 text-xs text-slate-400">
                    ({Math.round((row.suggestions.find((s) => s.memberId === member.id)?.score ?? 0) * 100)}%)
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
